import React, { useState, useEffect } from 'react';
import useStore from '../Base/Store';
import WebAPI from '../Base/WebAPI';
import { ifNullOrEmpty, isEmptyObjectArray, isNullOrEmpty, isNullOrUndefined, safeBool, safeInt, safeString, tryJSONParse } from '../Base/Utils';
import config from '../../config.json';
import { getNotificationCount } from '../Base/DataProvider';
import shallow from 'zustand/shallow';
import { DEFAULT_COUNTRY_CODE, INVOICE_COPY_HEADINGS, INVOICE_FORMAT_PRESTIGE_A5, TAX_REG_COMPOSITION_SCHEME, VOUCHER_TYPES } from '../Base/Common';
import { INVOICE_PAYMENT_QR_MODE_DYNAMIC } from '../Base/PaymentQRCodeHelper';

const AuthContext = React.createContext({
    token: '',
    register: async (userData) => { },
    updateContextState: () => { },
    login: async (email, password) => { },
    logout: () => { },
    isLoggedIn: false,
    currentUser: {},
    currentSubscriber: {},
    defaultWarehouse: null,
    defaultWarehouseId: 0,
    settings: {},
    updateSettings: (settings) => { },
    appModules: [{}],
    masterData: [{}],
    warehouseList: [{}],
    setWarehouseList: (warehouseList) => { },
    unitConversionList: [{}],
    updateUnitConversionList: (uom) => { },
    setSubscriberDetails: async (userSubscriberData) => { },
    moduleAccess: (appModuleId) => { },
    setSubscriberSessionData: (subscriberData) => { },
    customFields: [{}],
    refreshCustomFields: () => { },
    appVersion: ''
});

export const AuthContextProvider = (props) => {
    const [token, setToken] = useState(null);
    const [appVersion, setAppVersion] = useState('');
    const [currentUser, setCurrentUser] = useState({});
    const [currentSubscriber, setCurrentSubscriber] = useState({});
    const [warehouseList, setWarehouseList] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
    const [unitConversionList, setUnitConversionList] = useState([]);
    const [defaultWarehouse, setDefaultWarehouse] = useState(null);
    const [defaultWarehouseId, setDefaultWarehouseId] = useState(0);
    const [appModules, setAppModules] = useState([{}]);
    const [masterData, setMasterData] = useState([{}]);
    const [settings, setSettings] = useState({});
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const showAlert = useStore(state => state.showAlert);
    const closeAlert = useStore(state => state.closeAlert);
    const [notificationCount, setNotificationCount] = useStore(state => [state.notificationCount, state.setNotificationCount], shallow);
    const [appVersionCheck, setAppVersionCheck] = useStore(state => [state.appVersionCheck, state.setAppVersionCheck], shallow);

    const registerHandler = async (userData) => {
        try {
            const response = await WebAPI.post('users/register', userData);
            let token = null;

            if (!!response.data.code && response.data.code > 0) {
                setToken(null);
                setIsLoggedIn(false);
                showAlert(response.data.message, 'error');
            } else {
                closeAlert();
                setCurrentUser(response.data);
                // if (!!response.data.userAppModuleAccess) {
                //     setAppModules(response.data.userAppModuleAccess);
                // }
                if (!isNullOrUndefined(response.data.userDetails)) {
                    token = response.data.userDetails.accessToken;
                    setToken(token);
                    loadMasterData(token);
                }
            }
            return token;

        } catch (error) {
            showAlert('Internal server error.', 'error');
            console.log(error);
            return '';
        }
    }

    const loadMasterData = async (token) => {
        try {
            var masterDataList = await WebAPI.get('/MasterData/GetMasterDataList', { headers: { "AccessToken": token } });
            setMasterData(masterDataList.data);
        } catch (error) {
            showAlert('Failed to load master data. Internal server error.', 'error');
            console.log(error);
        }
    }

    const loadWarehouseList = async (subscriberdId, userDetails) => {
        try {
            var response = await WebAPI.get('/Warehouse/GetWarehouseList/' + subscriberdId, { headers: { "AccessToken": userDetails.accessToken } });
            // filter out warehouses only to the ones that the user has access to in userDetails.warehouseList which is an array of warehouse IDs
            const filteredWarehouses = response.data.filter(w => userDetails.warehouseIds.includes(w.id));
            setWarehouseList(filteredWarehouses);
            const defaultWarehouse = filteredWarehouses.find(w => w.id === userDetails.defaultWarehouseId) || filteredWarehouses[0];
            setDefaultWarehouse(defaultWarehouse);
            setDefaultWarehouseId(safeInt(defaultWarehouse?.id));
        } catch (error) {
            showAlert('Failed to load Warehouse data. Internal server error.', 'error');
            console.log(error);
        }
    }

    const loadUnitConversions = async (token) => {
        try {
            var response = await WebAPI.get('/UOM/UnitConversionList/', { headers: { "AccessToken": token } });
            setUnitConversionList(response.data);
        } catch (error) {
            showAlert('Failed to load UnitConversionList. Internal server error.', 'error');
            console.log(error);
        }
    }

    const loadCustomFields = async (token) => {
        try {
            var response = await WebAPI.get('/GenericTemplate/GenericTemplateList/' + 0, { headers: { "AccessToken": token } });
            if (!isEmptyObjectArray(response.data)) {
                let customFields = response.data.filter(f => safeInt(f.templateTypeId) >= 2002 && safeInt(f.templateTypeId) <= 2014);
                setCustomFields(customFields);
            }
        } catch (error) {
            showAlert('Failed to load GenericTemplateList. Internal server error.', 'error');
            console.log(error);
        }
    }

    const loadCustomFieldDefinitions = async (accessToken) => {
        try {
            // Load all custom field definitions
            const response = await WebAPI.get(`/SubscriberFields?entityName=*`, {
                headers: { "AccessToken": accessToken }
            });
            if (safeInt(response?.data.code) > 0) {
                showAlert(response.data.message, 'error');
                setCustomFieldDefinitions([]);
            } else {
                setCustomFieldDefinitions(response?.data || []);
            }
        } catch (error) {
            showAlert('Failed to load CustomFieldDefinitions. Internal server error.', 'error');
            console.log(error);
        }
    }

    const refreshCustomFieldDefinitionsHandler = async () => {
        loadCustomFieldDefinitions(token);
    }

    const handleUpdateUnitConversionUpdate = (uom, remove = false) => {
        var updatedValues = [...unitConversionList];
        var foundIndex = updatedValues.findIndex(u => u.id === uom.id);
        if (foundIndex > -1) {
            if (remove) {
                updatedValues.splice(foundIndex, 1)
            } else {
                updatedValues[foundIndex] = uom;
            }
        } else {
            updatedValues.push(uom);
        }
        setUnitConversionList(updatedValues);
    }

    const loginHandler = async (email, password) => {
        try {
            const response = await WebAPI.post('Users/Login', { emailAddress: email, password: password, applicationId: config.APPLICATION_ID });
            if (!!response.data.code && response.data.code > 0) {
                setToken(null);
                showAlert(response.data.message, 'error');
            } else {
                setSubscriberSessionDataHandler(response);

                let serverVersion = safeString(response?.data?.appVersion)
                if (!isNullOrEmpty(serverVersion)) {
                    setAppVersion(serverVersion);
                    if (config.ENVIRONMENT === "PROD" || config.ENVIRONMENT === "DEV") {
                        if (config.VERSION !== serverVersion) {
                            setTimeout(() => {
                                setAppVersionCheck(true);
                            }, 500);
                        } else {
                            setAppVersionCheck(false);
                        }
                    }
                }
            }
            if (!isNullOrUndefined(response)) {
                return response.data;
            } else {
                return null;
            }

        } catch (error) {
            showAlert('Internal server error.', 'error');
            console.log(error);
        }
    }

    const refreshCustomFieldsHandler = async () => {
        try {
            var response = await WebAPI.get('/GenericTemplate/GenericTemplateList/' + 0, { headers: { "AccessToken": token } });
            if (!isEmptyObjectArray(response.data)) {
                let customFields = response.data.filter(f => safeInt(f.templateTypeId) >= 2002 && safeInt(f.templateTypeId) <= 2014);
                setCustomFields(customFields);
            }
        } catch (error) {
            showAlert('Failed to load GenericTemplateList. Internal server error.', 'error');
            console.log(error);
        }
    }

    const setSubscriberSessionDataHandler = (response) => {
        closeAlert();
        const userDetails = response.data.userDetails;
        setCurrentUser(userDetails);
        if (!!response.data.subscriberDetails) {
            let subscriberDetails = response.data.subscriberDetails;
            let billingAddress = subscriberDetails.addressList.filter(a => a.addressType === 'Billing');
            if (!isNullOrUndefined(billingAddress) && billingAddress.length > 0) {
                subscriberDetails.countryCode = billingAddress[0].countryCode || DEFAULT_COUNTRY_CODE;
            }

            setCurrentSubscriber(subscriberDetails);
            // if (subscriberDetails.subscriberSettings.isMultiWarehouse) {
            loadWarehouseList(subscriberDetails.id, userDetails);
            // } else {
            //     let defaultWarehouse = subscriberDetails.warehouseList[0];
            //     setDefaultWarehouse(defaultWarehouse);
            //     setDefaultWarehouseId(safeInt(defaultWarehouse?.id));
            // }
            // Load Unit conversions
            loadUnitConversions(userDetails.accessToken);
            loadCustomFields(userDetails.accessToken);
            loadCustomFieldDefinitions(userDetails.accessToken);

            if (!isNullOrUndefined(response.data.subscriberDetails.subscriberSettings)) {
                var settings = response.data.subscriberDetails.subscriberSettings;
                if (!isNullOrEmpty(settings.additionalDetails)) {
                    const additionalDetailData = JSON.parse(settings.additionalDetails);
                    settings.orderSequenceLeadingZeroes = isNullOrUndefined(additionalDetailData.orderSequenceLeadingZeroes) ? 5 : safeInt(additionalDetailData.orderSequenceLeadingZeroes);
                    settings.preventNegativeStock = safeBool(additionalDetailData.preventNegativeStock);
                    settings.allowFreeQuantity = safeBool(additionalDetailData.allowFreeQuantity);
                    settings.includeReferencePrice = safeBool(additionalDetailData.includeReferencePrice);
                    settings.taxInclusivePricing = safeBool(additionalDetailData.taxInclusivePricing);
                    settings.showEInvoiceAlerts = safeBool(additionalDetailData.showEInvoiceAlerts, false);
                    settings.sendAutomaticSMS = safeBool(additionalDetailData.sendAutomaticSMS, true);
                    settings.enableBetaFeatures = safeBool(additionalDetailData.enableBetaFeatures, true);
                    settings.printItemCode = safeBool(additionalDetailData.printItemCode);
                    /*                     settings.autoHideSideMenu = safeBool(additionalDetailData.autoHideSideMenu, true);
                     */
                    settings.showQuantityDescription = safeBool(additionalDetailData.showQuantityDescription);
                    settings.showTotalQuantityDescription = safeBool(additionalDetailData.showTotalQuantityDescription);
                    settings.enableSalesPersonModule = safeBool(additionalDetailData.enableSalesPersonModule);
                    settings.enableCess = safeBool(additionalDetailData.enableCess);

                    settings.showOverrides = safeBool(additionalDetailData.showOverrides);
                    settings.deafultInvoiceCashBill = safeBool(additionalDetailData.deafultInvoiceCashBill);

                    settings.enableInvoicePrefixEdit = safeBool(additionalDetailData.enableInvoicePrefixEdit, true);
                    settings.enableInvoiceNumberEdit = safeBool(additionalDetailData.enableInvoiceNumberEdit, true);

                    settings.enableQuotationPrefixEdit = safeBool(additionalDetailData.enableQuotationPrefixEdit, false);
                    settings.enableQuotationNumberEdit = safeBool(additionalDetailData.enableQuotationNumberEdit, true);

                    settings.enableProformaInvoicePrefixEdit = safeBool(additionalDetailData.enableProformaInvoicePrefixEdit, false);
                    settings.enableProformaInvoiceNumberEdit = safeBool(additionalDetailData.enableProformaInvoiceNumberEdit, true);

                    settings.enableSalesOrderPrefixEdit = safeBool(additionalDetailData.enableSalesOrderPrefixEdit, false);
                    settings.enableSalesOrderNumberEdit = safeBool(additionalDetailData.enableSalesOrderNumberEdit, true);

                    settings.enableStockMovementsPrefixEdit = safeBool(additionalDetailData.enableStockMovementsPrefixEdit, false);
                    settings.enableStockMovementsNumberEdit = safeBool(additionalDetailData.enableStockMovementsNumberEdit, true);

                    settings.enableCreditNotePrefixEdit = safeBool(additionalDetailData.enableCreditNotePrefixEdit, false);
                    settings.enableCreditNoteNumberEdit = safeBool(additionalDetailData.enableCreditNoteNumberEdit, true);

                    settings.enableDebitNotePrefixEdit = safeBool(additionalDetailData.enableDebitNotePrefixEdit, false);
                    settings.enableDebitNoteNumberEdit = safeBool(additionalDetailData.enableDebitNoteNumberEdit, true);

                    settings.enablePurchaseOrderPrefixEdit = safeBool(additionalDetailData.enablePurchaseOrderPrefixEdit, false);
                    settings.enablePurchaseOrderNumberEdit = safeBool(additionalDetailData.enablePurchaseOrderNumberEdit, true);

                    settings.printPaymentInfo = safeBool(additionalDetailData.printPaymentInfo);
                    settings.printCustomerContactInfo = safeBool(additionalDetailData.printCustomerContactInfo, true);
                    settings.invoicePaymentQRCodeMode = safeString(additionalDetailData.invoicePaymentQRCodeMode, INVOICE_PAYMENT_QR_MODE_DYNAMIC);
                    settings.allowToChangeOrderDate = safeBool(additionalDetailData.allowToChangeOrderDate);
                    settings.printTotalPendingAmount = safeBool(additionalDetailData.printTotalPendingAmount);
                    settings.watermarkLabel = safeString(additionalDetailData.watermarkLabel);
                    settings.waterMarkAngle = safeInt(additionalDetailData.waterMarkAngle);
                    settings.invoiceCopyHeading1 = ifNullOrEmpty(additionalDetailData.invoiceCopyHeading1, INVOICE_COPY_HEADINGS[0]);
                    settings.invoiceCopyHeading2 = ifNullOrEmpty(additionalDetailData.invoiceCopyHeading2, INVOICE_COPY_HEADINGS[1]);
                    settings.invoiceCopyHeading3 = ifNullOrEmpty(additionalDetailData.invoiceCopyHeading3, INVOICE_COPY_HEADINGS[2]);
                    settings.invoiceCopyHeading4 = ifNullOrEmpty(additionalDetailData.invoiceCopyHeading4, INVOICE_COPY_HEADINGS[3]);
                    settings.invoiceCopyHeading5 = ifNullOrEmpty(additionalDetailData.invoiceCopyHeading5, INVOICE_COPY_HEADINGS[4]);
                    settings.enableDeliveryChallanPrefixEdit = safeBool(additionalDetailData.enableDeliveryChallanPrefixEdit, true);
                    settings.enableDeliveryChallanNumberEdit = safeBool(additionalDetailData.enableDeliveryChallanNumberEdit, true);
                    settings.AllowTaxRateEdit = ifNullOrEmpty(additionalDetailData.AllowTaxRateEdit, true);
                    settings.validateHSNCode = ifNullOrEmpty(additionalDetailData.validateHSNCode, true);
                    settings.hideSealAndSignature = ifNullOrEmpty(additionalDetailData.hideSealAndSignature, false)
                    settings.HSNCodeSize = ifNullOrEmpty(additionalDetailData.HSNCodeSize, "");
                    settings.defaultCashBillCustomer = safeInt(additionalDetailData.defaultCashBillCustomer);
                    settings.loginDisplayPage = ifNullOrEmpty(additionalDetailData.loginDisplayPage, "");
                    settings.sortOrderItemsAlphabetically = safeBool(additionalDetailData.sortOrderItemsAlphabetically, false);
                    settings.taxIdentifierLabel = safeString(additionalDetailData.taxIdentifierLabel, 'GSTIN');

                    VOUCHER_TYPES.forEach(voucherType => {
                        settings[`${voucherType.key}FormatSettings`] = tryJSONParse(additionalDetailData[`${voucherType.key}FormatSettings`]);
                        settings[`${voucherType.key}FormatColumnDefination`] = tryJSONParse(additionalDetailData[`${voucherType.key}FormatColumnDefination`]);
                    });

                    settings.POSinvoiceSettings = tryJSONParse(additionalDetailData.POSinvoiceSettings);
                    settings[`invoiceFormatSettings_${INVOICE_FORMAT_PRESTIGE_A5}`] = tryJSONParse(additionalDetailData[`invoiceFormatSettings_${INVOICE_FORMAT_PRESTIGE_A5}`]);

                    if (isNullOrUndefined(additionalDetailData.includeTaxSummaryQuotation)) {
                        settings.includeTaxSummaryQuotation = true;
                    } else {
                        settings.includeTaxSummaryQuotation = safeBool(additionalDetailData.includeTaxSummaryQuotation);
                    }

                    if (response.data.subscriberDetails.taxRegistrationTypeId === TAX_REG_COMPOSITION_SCHEME) {
                        settings.invoiceDocumentHeading = ifNullOrEmpty(additionalDetailData.invoiceDocumentHeading, "Bill of Supply")
                    } else {
                        settings.invoiceDocumentHeading = ifNullOrEmpty(additionalDetailData.invoiceDocumentHeading, "TAX INVOICE")
                    }
                    settings.quotationDocumentHeading = ifNullOrEmpty(additionalDetailData.quotationDocumentHeading, "Quotation");
                    settings.proformaInvoiceDocumentHeading = ifNullOrEmpty(additionalDetailData.proformaInvoiceDocumentHeading, "Proforma Invoice");

                    settings.includeTaxSummaryDeliveryChallan = safeBool(additionalDetailData.includeTaxSummaryDeliveryChallan);
                    settings.includeAmountDeliveryChallan = safeBool(additionalDetailData.includeAmountDeliveryChallan);

                    settings.includeTaxSummaryPurchaseOrder = safeBool(additionalDetailData.includeTaxSummaryPurchaseOrder, true);
                    settings.includeAmountPurchaseOrder = safeBool(additionalDetailData.includeAmountPurchaseOrder, true);

                } else {
                    /*  settings.autoHideSideMenu = true; */
                    settings.showEInvoiceAlerts = false;
                    settings.enableBetaFeatures = true;
                    settings.enableInvoicePrefixEdit = true;
                    settings.invoicePaymentQRCodeMode = INVOICE_PAYMENT_QR_MODE_DYNAMIC;
                    settings.enableQuotationPrefixEdit = true;
                    settings.enableStockMovementsPrefixEdit = false;
                    settings.enableStockMovementsNumberEdit = true;
                    settings.AllowTaxRateEdit = true;
                }
                if ((safeInt(response.data.subscriberDetails.businessTypeId) === 404 || safeInt(response.data.subscriberDetails.businessTypeId) === 405)) {
                    settings.isPharmacy = true;
                } else {
                    settings.isPharmacy = false;
                }
                setSettings(settings);
            }

        }
        if (!!response.data.userAppModuleAccess) {
            setAppModules(response.data.userAppModuleAccess);
        }
        setToken(userDetails.accessToken);
        if (!isNullOrUndefined(userDetails) && !isNullOrUndefined(userDetails.accessToken)) {
            setIsLoggedIn(true);
        }
        loadMasterData(userDetails.accessToken);

        getNotificationCount({ token: userDetails.accessToken }, setNotificationCount);

    }

    const logoutHandler = async () => {
        try {
            const requestOptions = {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(token)
            };
            const apiUrl = config.ENVIRONMENT === "PROD" ? config.SERVER_URL : config.SERVER_URL_LOCAL_HOST;
            const response = await fetch(apiUrl + 'Users/Logout', { ...requestOptions, credentials: 'include' });
            await response.text();
            setIsLoggedIn(false);
            setToken(null);
            setCurrentSubscriber(null);
            setAppModules(null);
        } catch (error) {
            console.log(error);
            setToken(null);
            // showAlert('Internal server error.', 'error');
        }
    }

    // Attempt silent refresh on mount to rehydrate session via httpOnly cookie
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();
        const TIMEOUT_MS = 7000;

        const tryRefresh = async () => {
            const apiUrl = config.ENVIRONMENT === "PROD" ? config.SERVER_URL : config.SERVER_URL_LOCAL_HOST;
            const url = apiUrl.endsWith('/') ? `${apiUrl}Users/Refresh` : `${apiUrl}/Users/Refresh`;
            const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

            try {
                const resp = await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    signal: controller.signal,
                    headers: { 'Accept': 'application/json' }
                });

                clearTimeout(timeout);

                if (!resp) return;

                // treat 204 / empty as non-refreshed
                if (resp.status === 204) {
                    if (mounted) {
                        setIsLoggedIn(false);
                        setToken(null);
                        setCurrentSubscriber(null);
                        setAppModules(null);
                    }
                    return;
                }

                if (!resp.ok) {
                    // non-OK (401/403/etc) -> clear session
                    if (mounted) {
                        setIsLoggedIn(false);
                        setToken(null);
                        setCurrentSubscriber(null);
                        setAppModules(null);
                    }
                    return;
                }

                let body = null;
                try {
                    body = await resp.json();
                } catch (parseErr) {
                    if (mounted) {
                        setIsLoggedIn(false);
                        setToken(null);
                        setCurrentSubscriber(null);
                        setAppModules(null);
                    }
                    return;
                }

                if (!mounted || !body) return;

                if (body.code && body.code > 0) {
                    if (mounted) {
                        setIsLoggedIn(false);
                        setToken(null);
                        setCurrentSubscriber(null);
                        setAppModules(null);
                    }
                    return;
                }

                // assume backend returns same payload shape used by login
                setSubscriberSessionDataHandler({ data: body });
                //Check for any updates
                let serverVersion = safeString(body.appVersion)
                if (!isNullOrEmpty(serverVersion)) {
                    setAppVersion(serverVersion);
                    if (config.ENVIRONMENT === "PROD") {
                        if (config.VERSION !== serverVersion) {
                            setTimeout(() => {
                                setAppVersionCheck(true);
                            }, 500);
                        } else {
                            setAppVersionCheck(false);
                        }
                    }
                }

            } catch (err) {
                if (err.name === 'AbortError') {
                    // timeout/abort - do nothing noisy
                    console.log('Refresh aborted/timed out');
                } else {
                    console.log('silent refresh failed', err);
                }
            } finally {
                try { clearTimeout(); } catch (e) { /* no-op */ }
            }
        };

        if (!window.location.pathname.startsWith('/docs') && !window.location.pathname.includes('payment') && !window.location.pathname.includes('payable') && !window.location.pathname.includes('upgradenow')) {
            tryRefresh();
        }

        return () => {
            mounted = false;
            try { controller.abort(); } catch (e) { /* no-op */ }
        };
    }, []);

    const updateSettingsHandler = async (settings) => {
        setSettings(settings);
    }

    const handleUpdateSubscriberDetails = async (userData) => {
        if (!!userData.data.userAppModuleAccess) {
            setAppModules(userData.data.userAppModuleAccess);
        }
        setCurrentUser(userData.data.userDetails);
        if (!!userData.data.subscriberDetails) {
            setCurrentSubscriber(userData.data.subscriberDetails);
            if (!isNullOrUndefined(userData.data.subscriberDetails.warehouseList[0])) {
                let defaultWarehouse = userData.data.subscriberDetails.warehouseList[0];
                setDefaultWarehouse(defaultWarehouse);
                setDefaultWarehouseId(safeInt(defaultWarehouse?.id));
            }
            if (!!userData.data.subscriberDetails.subscriberSettings) {
                setSettings(userData.data.subscriberDetails.subscriberSettings);
            }
        }
        setToken(userData.data.userDetails.accessToken);
        if (!isNullOrUndefined(userData.data.userDetails) && !isNullOrUndefined(userData.data.userDetails.accessToken)) {
            setIsLoggedIn(true);
        }
        await loadMasterData(userData.data.userDetails.accessToken);
    }

    const moduleAccess = (appModuleId) => {
        try {
            let appModule = appModules.find(m => m.appModuleId === appModuleId);
            if (!isNullOrUndefined(appModule)) {
                return {
                    allowView: safeBool(appModule.allowView),
                    allowEdit: safeBool(appModule.allowEdit),
                    allowAdd: safeBool(appModule.allowAdd),
                    allowDelete: safeBool(appModule.allowDelete),
                }
            } else {
                return {
                    allowView: false,
                    allowEdit: false,
                    allowAdd: false,
                    allowDelete: false,
                }
            }
        } catch (error) {
            return {
                allowView: false,
                allowEdit: false,
                allowAdd: false,
                allowDelete: false
            }
        }
    }

    const updateContextStatehandler = () => {
        setIsLoggedIn(true);
    }

    const contextValue = {
        token: token,
        isLoggedIn: isLoggedIn,
        register: registerHandler,
        updateContextState: updateContextStatehandler,
        login: loginHandler,
        logout: logoutHandler,
        currentUser: currentUser,
        currentSubscriber: currentSubscriber,
        defaultWarehouse: defaultWarehouse,
        defaultWarehouseId: defaultWarehouseId,
        settings: settings,
        updateSettings: updateSettingsHandler,
        appModules: appModules,
        masterData: masterData,
        setWarehouseList: setWarehouseList,
        warehouseList: warehouseList,
        unitConversionList: unitConversionList,
        updateUnitConversionList: handleUpdateUnitConversionUpdate,
        moduleAccess: moduleAccess,
        setSubscriberDetails: handleUpdateSubscriberDetails,
        setSubscriberSessionData: setSubscriberSessionDataHandler,
        customFields: customFields,
        refreshCustomFields: refreshCustomFieldsHandler,
        customFieldDefinitions: customFieldDefinitions,
        refreshCustomFieldDefinitions: refreshCustomFieldDefinitionsHandler,

        appVersion: appVersion
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {props.children}
        </AuthContext.Provider>
    )
};

export default AuthContext;